import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { IBrandMenuOrderItem, IMUErrorResponse } from '../../types/brandMenu';
import { AxiosResponse } from 'axios';
import {
  setCurrentTransactionItemStatus,
  setOrderError,
  setErrorCartItemId,
  setMenuModuleCartErrors,
  resetMenuModuleErrorState,
} from '../../reducers/orderSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import { selectOrderErrorCartItemId } from '../../selectors/order';
import { selectFeatureFlagMenuModuleErrorUi } from '../../redux/features/config/config.selector';
import { findOrderIndex } from '../../utils/menuUnification.utils';
import { normalizeMenuModuleError } from '../../utils/menuModuleError';

const useMenuUnificationOrderHandlers = () => {
  const dispatch = useAppDispatch();
  const errorCartItemId = useAppSelector(selectOrderErrorCartItemId);
  const menuModuleErrorUiEnabled = useAppSelector(
    selectFeatureFlagMenuModuleErrorUi
  );

  const menuUnificationOrderFailureCallback = ({
    validCartItemIds,
    error,
    menuModuleItems,
    menuModuleItemsCartItemIds,
  }: {
    validCartItemIds: number[];
    error: AxiosResponse;
    menuModuleItems: IBrandMenuOrderItem[];
    menuModuleItemsCartItemIds: number[];
  }) => {
    // Kill switch: with menu_module_error_ui off, reproduce the legacy
    // behavior — flag the first path-matched item (or the last valid item),
    // show the raw error_type, and never populate menuModuleCartErrors (so
    // the banner and node highlights stay dormant).
    if (!menuModuleErrorUiEnabled) {
      let legacyErrorCartItemId = -1;
      const legacyPath = error?.data?.detail?.[0]?.path;
      if (legacyPath) {
        const errorItemIndex = findOrderIndex(legacyPath, menuModuleItems);
        if (errorItemIndex >= 0)
          legacyErrorCartItemId = menuModuleItemsCartItemIds[errorItemIndex];
      }
      if (legacyErrorCartItemId < 0) {
        legacyErrorCartItemId =
          !errorCartItemId ||
          !validCartItemIds.find((id) => id === errorCartItemId)
            ? validCartItemIds[validCartItemIds.length - 1]
            : errorCartItemId;
      }
      dispatch(
        setCurrentTransactionItemStatus({
          cartItemId: String(legacyErrorCartItemId),
          orderStatus: OrderStatus.failed,
        })
      );
      dispatch(
        setOrderError(
          error?.status === 400
            ? (error?.data as IMUErrorResponse)?.error_type || ''
            : ''
        )
      );
      dispatch(setErrorCartItemId(legacyErrorCartItemId));
      return;
    }

    // Normalize every 400 error_type (unavailable_items / unmatched_items /
    // invalid_constraints) into a flat, de-duped list of per-node errors;
    // anything unrecognized degrades to a single path-less `unknown` error.
    const cartErrors = normalizeMenuModuleError(
      error?.data as IMUErrorResponse
    );

    // Resolve each failing node to its owning cart item (recording the id on
    // the error so the UI can highlight the exact line) and flag them all.
    const matchedCartItemIds: number[] = [];
    cartErrors.forEach((cartError) => {
      const errorItemIndex = findOrderIndex(
        cartError.nodePath,
        menuModuleItems
      );
      if (errorItemIndex >= 0) {
        const cartItemId = menuModuleItemsCartItemIds[errorItemIndex];
        cartError.cartItemId = cartItemId;
        if (!matchedCartItemIds.includes(cartItemId)) {
          matchedCartItemIds.push(cartItemId);
        }
      }
    });

    // Flag exactly the cart items the error paths resolved to. When nothing
    // resolves to a cart line (paths that don't match the cart, path-less
    // `unknown` errors, or no structured error at all), fall back to flagging
    // the last valid item so the failure is always visible on the cart; the
    // order-level summary still carries the raw diagnostics.
    let cartItemIdsToFlag = matchedCartItemIds;
    if (cartItemIdsToFlag.length === 0) {
      const fallbackId =
        !errorCartItemId ||
        !validCartItemIds.find((id) => id === errorCartItemId)
          ? validCartItemIds[validCartItemIds.length - 1]
          : errorCartItemId;
      if (fallbackId !== undefined) {
        cartItemIdsToFlag = [fallbackId];
      }
    }

    cartItemIdsToFlag.forEach((cartItemId) => {
      dispatch(
        setCurrentTransactionItemStatus({
          cartItemId: String(cartItemId),
          orderStatus: OrderStatus.failed,
        })
      );
    });

    // Reason-specific copy for the primary failing node; fall back to the
    // response's error_message (PRVA-13232) and then the raw error_type when
    // the shape is unrecognized so nothing is silently dropped.
    const errorMessage =
      error?.status === 400
        ? cartErrors[0]?.message ||
          (error?.data as IMUErrorResponse)?.error_message ||
          (error?.data as IMUErrorResponse)?.error_type ||
          ''
        : '';

    dispatch(setMenuModuleCartErrors(cartErrors));
    dispatch(setOrderError(errorMessage));
    dispatch(
      setErrorCartItemId(cartItemIdsToFlag.length ? cartItemIdsToFlag[0] : -1)
    );
  };

  // Invoked on every 200 from the menu module (each cart-change send, not just
  // the final order), so keep it side-effect-light: no success toast here.
  const menuUnificationOrderSuccessCallback = () => {
    // Clear any lingering error highlights from a prior failed attempt. With
    // the flag off nothing was populated, and legacy behavior never reset on
    // success — so stay inert.
    if (menuModuleErrorUiEnabled) {
      dispatch(resetMenuModuleErrorState());
    }
  };

  return {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  };
};

export default useMenuUnificationOrderHandlers;
